/**
 * Smart contact bar for the candidate detail surface.
 *
 * Renders one-click Call / WhatsApp / Email / Copy-link buttons that are
 * auto-disabled when the underlying data is missing. Every click is logged
 * to recruiter_activities via /api/audit so the team can see who reached
 * out (and when) without leaving the candidate view.
 *
 * Also surfaces a "Recover email from resume" button when the email field
 * is empty but a resume_text exists — calls the candidate-hygiene endpoint
 * to regex-scan the resume and persist the first plausible address.
 *
 * Inline edit-email pencil lets recruiters fix bad / missing emails on the
 * spot (audited via candidate_changes).
 */
import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, Copy, Sparkles, Pencil, Save, X, Loader2, MapPin } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";

function digits(s) { return (s || "").replace(/\D+/g, ""); }

async function logContact(candidateId, channel, target) {
    try {
        await api.post("/audit/contact", {
            candidate_id: candidateId,
            channel, // "call" | "whatsapp" | "email" | "copy"
            target,
        });
    } catch { /* non-fatal */ }
}

export default function CandidateContactBar({ candidate, onUpdated }) {
    const [editing, setEditing] = useState(false);
    const [email, setEmail] = useState(candidate?.email || "");
    const [recovering, setRecovering] = useState(false);
    const [savingEmail, setSavingEmail] = useState(false);

    const phone = candidate?.phone || "";
    const phoneDigits = digits(phone);
    const hasEmail = !!candidate?.email;
    const hasPhone = !!phone;

    const onCall = () => {
        if (!hasPhone) return;
        logContact(candidate.id, "call", phone);
        window.location.href = `tel:${phone}`;
    };

    const onWhats = () => {
        if (!phoneDigits) return;
        logContact(candidate.id, "whatsapp", phoneDigits);
        const url = `https://wa.me/${phoneDigits}`;
        window.open(url, "_blank", "noopener");
    };

    const onEmail = () => {
        if (!hasEmail) return;
        logContact(candidate.id, "email", candidate.email);
        const subject = encodeURIComponent(`Hi ${candidate.name || "there"}`);
        window.location.href = `mailto:${candidate.email}?subject=${subject}`;
    };

    const onCopy = async () => {
        const link = `${window.location.origin}/candidates/${candidate.id}`;
        try {
            await navigator.clipboard.writeText(link);
            toast.success("Profile link copied");
            logContact(candidate.id, "copy", link);
        } catch { toast.error("Copy failed"); }
    };

    const recoverEmail = async () => {
        setRecovering(true);
        try {
            const { data } = await api.post(`/candidates/${candidate.id}/recover-email`);
            if (data.ok && data.changed) {
                toast.success(`Email recovered from resume: ${data.email}`);
                onUpdated?.();
            } else if (data.ok && !data.changed) {
                toast.info("Candidate already has an email");
            } else if (data.reason === "no_email_found") {
                toast.error("No email found in resume — please add it manually");
                setEditing(true);
            } else if (data.reason === "no_resume_text") {
                toast.error("No resume text on file — add the email manually");
                setEditing(true);
            }
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setRecovering(false); }
    };

    const saveEmail = async () => {
        const e = (email || "").trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Invalid email"); return; }
        setSavingEmail(true);
        try {
            await api.patch(`/candidates/${candidate.id}/email`, { email: e });
            toast.success("Email updated");
            setEditing(false);
            onUpdated?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSavingEmail(false); }
    };

    const btnBase = "h-9 px-3 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest border transition-colors";
    const btnLive = "border-ikb bg-white text-ikb hover:bg-ikb hover:text-white cursor-pointer";
    const btnDead = "border-border bg-secondary/40 text-muted-foreground cursor-not-allowed";

    return (
        <section className="border border-border bg-white" data-testid="contact-bar">
            <div className="p-3 sm:p-4 space-y-3">
                {/* Top row: location + experience teaser */}
                <div className="text-[11px] font-mono text-muted-foreground inline-flex items-center gap-3 flex-wrap">
                    {candidate.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {candidate.location}</span>}
                    {typeof candidate.total_experience_years === "number" && <span>{candidate.total_experience_years}y exp</span>}
                </div>

                {/* Email row — show value, with edit/recover */}
                <div className="flex items-center gap-2 flex-wrap">
                    <Mail className="w-3.5 h-3.5 text-foreground/60 shrink-0" />
                    {editing ? (
                        <>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="name@company.com"
                                autoFocus
                                onKeyDown={(e) => { if (e.key === "Escape") { setEditing(false); setEmail(candidate.email || ""); } if (e.key === "Enter") saveEmail(); }}
                                className="h-8 px-2 border border-border bg-white text-sm rounded-none outline-none focus:border-ikb min-w-0 flex-1"
                                data-testid="contact-email-input"
                            />
                            <button onClick={saveEmail} disabled={savingEmail} className="text-[10px] font-mono uppercase tracking-widest bg-ikb hover:bg-[#001f7a] text-white px-2 py-1.5 inline-flex items-center gap-1 disabled:opacity-50" data-testid="contact-email-save">
                                <Save className="w-3 h-3" /> {savingEmail ? "Saving…" : "Save"}
                            </button>
                            <button onClick={() => { setEditing(false); setEmail(candidate.email || ""); }} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1.5 inline-flex items-center gap-1" data-testid="contact-email-cancel">
                                <X className="w-3 h-3" />
                            </button>
                        </>
                    ) : hasEmail ? (
                        <>
                            <span className="text-sm break-all" data-testid="contact-email-value">{candidate.email}</span>
                            <button onClick={() => setEditing(true)} className="text-[10px] font-mono uppercase tracking-widest text-ikb hover:underline inline-flex items-center gap-0.5" data-testid="contact-email-edit">
                                <Pencil className="w-2.5 h-2.5" /> Edit
                            </button>
                        </>
                    ) : (
                        <>
                            <span className="text-sm text-rose-700" data-testid="contact-email-missing">No email on file</span>
                            <button
                                onClick={recoverEmail}
                                disabled={recovering}
                                className="text-[10px] font-mono uppercase tracking-widest border border-ikb text-ikb bg-white px-2 py-1 inline-flex items-center gap-1 hover:bg-ikb hover:text-white disabled:opacity-50"
                                data-testid="contact-email-recover"
                            >
                                {recovering ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                {recovering ? "Scanning resume…" : "Recover from resume"}
                            </button>
                            <button onClick={() => setEditing(true)} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1 inline-flex items-center gap-1 hover:bg-secondary" data-testid="contact-email-add">
                                <Pencil className="w-3 h-3" /> Add manually
                            </button>
                        </>
                    )}
                </div>

                {/* Phone row */}
                <div className="flex items-center gap-2 flex-wrap">
                    <Phone className="w-3.5 h-3.5 text-foreground/60 shrink-0" />
                    {hasPhone ? (
                        <span className="text-sm">{phone}</span>
                    ) : (
                        <span className="text-sm text-muted-foreground">No phone on file</span>
                    )}
                </div>

                {/* Quick actions */}
                <div className="flex flex-wrap gap-2 pt-1" data-testid="contact-actions">
                    <button onClick={onCall} disabled={!hasPhone} className={`${btnBase} ${hasPhone ? btnLive : btnDead}`} data-testid="contact-call">
                        <Phone className="w-3.5 h-3.5" /> Call
                    </button>
                    <button onClick={onWhats} disabled={!phoneDigits} className={`${btnBase} ${phoneDigits ? btnLive : btnDead}`} data-testid="contact-whatsapp">
                        <WhatsAppIcon className="w-3.5 h-3.5" /> WhatsApp
                    </button>
                    <button onClick={onEmail} disabled={!hasEmail} className={`${btnBase} ${hasEmail ? btnLive : btnDead}`} data-testid="contact-email-btn">
                        <Mail className="w-3.5 h-3.5" /> Email
                    </button>
                    <button onClick={onCopy} className={`${btnBase} ${btnLive}`} data-testid="contact-copy">
                        <Copy className="w-3.5 h-3.5" /> Copy link
                    </button>
                </div>
            </div>
        </section>
    );
}
